import { FACEBOOK_APP_ID, FACEBOOK_API_VERSION } from "@/config/app";

export type FacebookAuthResponse = {
  accessToken: string;
  expiresIn: string;
  signedRequest: string;
  userID: string;
};

export type FacebookLoginResponse = {
  status: "connected" | "not_authorized" | "unknown";
  authResponse?: FacebookAuthResponse;
};

declare global {
  interface Window {
    fbAsyncInit?: () => void;
    FB?: {
      init: (params: {
        appId: string;
        cookie?: boolean;
        xfbml?: boolean;
        version: string;
      }) => void;
      AppEvents?: { logPageView: () => void };
      getLoginStatus: (
        callback: (response: FacebookLoginResponse) => void
      ) => void;
      login: (
        callback: (response: FacebookLoginResponse) => void,
        options?: { scope?: string; return_scopes?: boolean }
      ) => void;
      logout: (callback?: (response: FacebookLoginResponse) => void) => void;
      api: (
        path: string,
        paramsOrCallback?:
          | Record<string, unknown>
          | ((response: Record<string, unknown>) => void),
        callback?: (response: Record<string, unknown>) => void
      ) => void;
    };
  }
}

let loadPromise: Promise<NonNullable<typeof window.FB>> | null = null;

const injectFacebookScript = () => {
  if (document.getElementById("facebook-jssdk")) return;

  const script = document.createElement("script");
  script.id = "facebook-jssdk";
  script.async = true;
  script.defer = true;
  script.crossOrigin = "anonymous";
  script.src = "https://connect.facebook.net/vi_VN/sdk.js";
  document.body.appendChild(script);
};

export const FACEBOOK_SDK_MISSING_APP_ID = "FACEBOOK_SDK_MISSING_APP_ID";

/**
 * Load + init Facebook JS SDK (idempotent).
 */
export const initFacebookSdk = (): Promise<NonNullable<typeof window.FB>> => {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    if (!FACEBOOK_APP_ID) {
      reject(new Error(FACEBOOK_SDK_MISSING_APP_ID));
      return;
    }

    if (window.FB) {
      resolve(window.FB);
      return;
    }

    window.fbAsyncInit = () => {
      window.FB?.init({
        appId: FACEBOOK_APP_ID,
        cookie: true,
        xfbml: false,
        version: FACEBOOK_API_VERSION,
      });
      window.FB?.AppEvents?.logPageView();
      if (window.FB) resolve(window.FB);
      else reject(new Error("Facebook SDK init thất bại"));
    };

    injectFacebookScript();
  });

  return loadPromise;
};

export const facebookLogin = (
  scope = "public_profile,email"
): Promise<FacebookLoginResponse> => {
  return initFacebookSdk().then(
    (FB) =>
      new Promise((resolve) => {
        FB.login((response) => resolve(response), {
          scope,
          return_scopes: true,
        });
      })
  );
};

export const getFacebookProfile = (
  fields = "id,name,email,picture"
): Promise<Record<string, unknown>> => {
  return initFacebookSdk().then(
    (FB) =>
      new Promise((resolve, reject) => {
        FB.api("/me", { fields }, (response) => {
          if ((response as any)?.error) {
            reject((response as any).error);
            return;
          }
          resolve(response);
        });
      })
  );
};
